import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import api from '../../../utils/api';
import { useAuth } from '../../../App';
import {
  ArrowRightLeft, Plus, Loader2, Trash2, Pencil, CheckCircle2,
  RotateCcw, X, Building2, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const eur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const MONTH_NAMES = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const monthLabel = (ym) => {
  const [y, m] = (ym || '').split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1] || m} ${y}`;
};
const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
};

const EMPTY_FORM = { payer_company_id: '', beneficiary_company_id: '', amount: '', payment_date: new Date().toISOString().slice(0, 10), description: '', supplier: '' };

function CompanySelect({ value, onChange, companies, placeholder, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="w-full px-3 py-2 border rounded-lg text-sm bg-white">
      <option value="">{placeholder}</option>
      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  );
}

export default function IntercompanyChargesTab({ year }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [companies, setCompanies] = useState([]);
  const [charges, setCharges] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editCharge, setEditCharge] = useState(null);
  const [markingKey, setMarkingKey] = useState(null);
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterPayer, setFilterPayer] = useState('');
  const [filterBen, setFilterBen] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [chargesRes, summaryRes] = await Promise.all([
        api.get(`/intercompany-charges?year=${year}`),
        api.get(`/intercompany-charges/summary?year=${year}`),
      ]);
      setCharges(chargesRes.data.charges || []);
      setSummary(summaryRes.data.summary || []);
    } catch (e) {
      toast.error('Errore nel caricamento dei ribaltamenti');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.payer_company_id || !form.beneficiary_company_id) { toast.error('Seleziona entrambe le società'); return; }
    if (form.payer_company_id === form.beneficiary_company_id) { toast.error('Le due società devono essere diverse'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Inserisci un importo valido'); return; }
    if (!form.description.trim()) { toast.error('Inserisci una descrizione'); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount), supplier: form.supplier || null };
      if (editCharge) {
        await api.put(`/intercompany-charges/${editCharge.id}`, payload);
        toast.success('Movimento aggiornato');
      } else {
        await api.post('/intercompany-charges', payload);
        toast.success('Movimento registrato');
      }
      setForm(EMPTY_FORM); setShowForm(false); setEditCharge(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (charge) => {
    if (!window.confirm(`Eliminare il movimento di ${eur(charge.amount)} (${charge.payer_company_name} → ${charge.beneficiary_company_name})?`)) return;
    try {
      await api.delete(`/intercompany-charges/${charge.id}`);
      toast.success('Movimento eliminato');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const handleMarkInvoiced = async (row, invoiced) => {
    const key = `${row.month}_${row.payer_company_id}_${row.beneficiary_company_id}`;
    setMarkingKey(key);
    try {
      await api.post('/intercompany-charges/mark-invoiced', {
        payer_company_id: row.payer_company_id,
        beneficiary_company_id: row.beneficiary_company_id,
        month: row.month,
        invoiced,
      });
      toast.success(invoiced ? 'Mese segnato come fatturato' : 'Mese riportato a "da fatturare"');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setMarkingKey(null);
    }
  };

  const openEdit = (charge) => {
    setEditCharge(charge);
    setForm({
      payer_company_id: charge.payer_company_id,
      beneficiary_company_id: charge.beneficiary_company_id,
      amount: String(charge.amount),
      payment_date: charge.payment_date,
      description: charge.description || '',
      supplier: charge.supplier || '',
    });
    setShowForm(true);
  };

  const availableMonths = useMemo(() => {
    const set = new Set(charges.map(c => (c.payment_date || '').slice(0, 7)).filter(Boolean));
    return [...set].sort().reverse();
  }, [charges]);

  const filteredCharges = useMemo(() => charges.filter(c => {
    if (filterMonth !== 'all' && (c.payment_date || '').slice(0, 7) !== filterMonth) return false;
    if (filterPayer && c.payer_company_id !== filterPayer) return false;
    if (filterBen && c.beneficiary_company_id !== filterBen) return false;
    return true;
  }), [charges, filterMonth, filterPayer, filterBen]);

  const summaryByMonth = useMemo(() => {
    const map = {};
    summary.forEach(r => { (map[r.month] = map[r.month] || []).push(r); });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [summary]);

  const yearTotal = useMemo(() => charges.reduce((s, c) => s + Number(c.amount || 0), 0), [charges]);
  const toInvoiceTotal = useMemo(() => charges.filter(c => !c.invoiced).reduce((s, c) => s + Number(c.amount || 0), 0), [charges]);

  if (loading) return <div className="text-center py-12 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Caricamento...</div>;

  return (
    <div className="space-y-4" data-testid="intercompany-tab">
      {/* Header totals + add button */}
      <Card className="p-3 bg-indigo-50 border-indigo-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2 text-indigo-900">
              <ArrowRightLeft className="h-4 w-4" />
              <span className="text-sm">Totale ribaltamenti {year}:</span>
              <span className="text-lg font-bold" data-testid="intercompany-year-total">{eur(yearTotal)}</span>
            </div>
            <div className="flex items-center gap-2 text-amber-800">
              <span className="text-sm">Da fatturare:</span>
              <span className="text-lg font-bold" data-testid="intercompany-to-invoice-total">{eur(toInvoiceTotal)}</span>
            </div>
          </div>
          <Button size="sm" onClick={() => { setEditCharge(null); setForm(EMPTY_FORM); setShowForm(true); }} data-testid="intercompany-add-btn">
            <Plus className="h-4 w-4 mr-1" /> Nuovo movimento
          </Button>
        </div>
      </Card>

      {/* Monthly summary per company pair */}
      {summaryByMonth.length > 0 && (
        <div className="space-y-3">
          {summaryByMonth.map(([month, rows]) => (
            <Card key={month} className="p-4" data-testid={`intercompany-summary-${month}`}>
              <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4 text-indigo-600" /> {monthLabel(month)}
              </h3>
              <div className="space-y-1.5">
                {rows.map((r, i) => {
                  const key = `${r.month}_${r.payer_company_id}_${r.beneficiary_company_id}`;
                  return (
                    <div key={i} className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2" data-testid={`intercompany-summary-row-${i}`}>
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 className="h-3.5 w-3.5 text-slate-400" />
                        <span className="font-medium text-slate-800">{r.payer_company_name}</span>
                        <ArrowRightLeft className="h-3.5 w-3.5 text-indigo-500" />
                        <span className="font-medium text-slate-800">{r.beneficiary_company_name}</span>
                        <span className="text-xs text-slate-500">({r.count} movim.)</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-slate-900">{eur(r.total)}</span>
                        {r.invoiced ? (
                          <span className="flex items-center gap-1.5">
                            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-medium">
                              <CheckCircle2 className="h-3 w-3" /> Fatturato
                            </span>
                            <button onClick={() => handleMarkInvoiced(r, false)} disabled={markingKey === key}
                              className="p-1 text-slate-400 hover:text-amber-600 rounded" title="Riporta a da fatturare"
                              data-testid={`intercompany-uninvoice-${i}`}>
                              {markingKey === key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                            </button>
                          </span>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => handleMarkInvoiced(r, true)} disabled={markingKey === key}
                            data-testid={`intercompany-mark-invoiced-${i}`}>
                            {markingKey === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle2 className="h-3 w-3 mr-1" /> Segna fatturato</>}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Filters + movements table */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-sm font-bold text-slate-800">Movimenti registrati</h3>
          <div className="flex flex-wrap items-center gap-2">
            <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="px-2 py-1.5 border rounded-lg text-xs bg-white" data-testid="intercompany-filter-month">
              <option value="all">Tutti i mesi</option>
              {availableMonths.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
            <select value={filterPayer} onChange={(e) => setFilterPayer(e.target.value)} className="px-2 py-1.5 border rounded-lg text-xs bg-white" data-testid="intercompany-filter-payer">
              <option value="">Pagante: tutte</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={filterBen} onChange={(e) => setFilterBen(e.target.value)} className="px-2 py-1.5 border rounded-lg text-xs bg-white" data-testid="intercompany-filter-beneficiary">
              <option value="">Beneficiaria: tutte</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {(filterMonth !== 'all' || filterPayer || filterBen) && (
              <button onClick={() => { setFilterMonth('all'); setFilterPayer(''); setFilterBen(''); }} className="p-1.5 text-slate-400 hover:text-slate-700" title="Reset filtri">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        {filteredCharges.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm" data-testid="intercompany-empty">
            <ArrowRightLeft className="h-8 w-8 mx-auto mb-2" />
            Nessun movimento di ribaltamento registrato per {year}
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr>
                <th className="text-left p-2 font-medium text-slate-600">Data</th>
                <th className="text-left p-2 font-medium text-slate-600">Pagante</th>
                <th className="text-left p-2 font-medium text-slate-600">Beneficiaria</th>
                <th className="text-left p-2 font-medium text-slate-600">Descrizione</th>
                <th className="text-left p-2 font-medium text-slate-600">Fornitore</th>
                <th className="text-right p-2 font-medium text-slate-600">Importo</th>
                <th className="text-center p-2 font-medium text-slate-600">Stato</th>
                <th className="text-right p-2 font-medium text-slate-600"></th>
              </tr></thead>
              <tbody className="divide-y">
                {filteredCharges.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50" data-testid={`intercompany-row-${c.id}`}>
                    <td className="p-2 whitespace-nowrap">{fmtDate(c.payment_date)}</td>
                    <td className="p-2 font-medium">{c.payer_company_name}</td>
                    <td className="p-2 font-medium">{c.beneficiary_company_name}</td>
                    <td className="p-2 text-slate-600 max-w-[280px] truncate" title={c.description}>{c.description}</td>
                    <td className="p-2 text-slate-500">{c.supplier || '-'}</td>
                    <td className="p-2 text-right font-semibold whitespace-nowrap">{eur(c.amount)}</td>
                    <td className="p-2 text-center">
                      {c.invoiced ? (
                        <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs">Fatturato</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs">Da fatturare</span>
                      )}
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(c)} className="p-1.5 text-slate-400 hover:text-blue-600 rounded" title="Modifica" data-testid={`intercompany-edit-${c.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      {isAdmin && (
                        <button onClick={() => handleDelete(c)} className="p-1.5 text-slate-400 hover:text-red-600 rounded" title="Elimina" data-testid={`intercompany-delete-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditCharge(null); setForm(EMPTY_FORM); } }}>
        <DialogContent className="max-w-lg" data-testid="intercompany-form-dialog">
          <DialogHeader>
            <DialogTitle>{editCharge ? 'Modifica movimento' : 'Nuovo movimento di ribaltamento'}</DialogTitle>
            <DialogDescription>Registra un pagamento fatto da una società per conto di un'altra</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Società pagante *</label>
                <CompanySelect value={form.payer_company_id} onChange={(v) => setForm(f => ({ ...f, payer_company_id: v }))}
                  companies={companies} placeholder="Chi ha pagato..." testId="intercompany-form-payer" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Società beneficiaria *</label>
                <CompanySelect value={form.beneficiary_company_id} onChange={(v) => setForm(f => ({ ...f, beneficiary_company_id: v }))}
                  companies={companies} placeholder="Per conto di..." testId="intercompany-form-beneficiary" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Data pagamento *</label>
                <input type="date" value={form.payment_date} onChange={(e) => setForm(f => ({ ...f, payment_date: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" required data-testid="intercompany-form-date" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Importo (€) *</label>
                <input type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0,00" className="w-full px-3 py-2 border rounded-lg text-sm" required data-testid="intercompany-form-amount" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Descrizione *</label>
              <input type="text" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Es. Fattura Enel giugno, canone software..." className="w-full px-3 py-2 border rounded-lg text-sm" required data-testid="intercompany-form-description" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Fornitore (opzionale)</label>
              <input type="text" value={form.supplier} onChange={(e) => setForm(f => ({ ...f, supplier: e.target.value }))}
                placeholder="Es. Enel, TIM..." className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="intercompany-form-supplier" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => { setShowForm(false); setEditCharge(null); setForm(EMPTY_FORM); }}>Annulla</Button>
              <Button type="submit" size="sm" disabled={saving} data-testid="intercompany-form-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {editCharge ? 'Salva modifiche' : 'Registra'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
